import { useTranslation } from 'react-i18next';

import DisplayTeamInfo from './DisplayTeamInfo';
import LineupPlayer from './LineupPlayer';
import ShowMatchDetails from './ShowMatchDetails';
import type { LineUpsFieldData } from './types';

import { useGetLineUpQuery, useGetMatchEventsQuery } from 'api/fspApi';
import type { LineUps as LineUp, MatchEvents } from 'types/ApiResponseInterfaces';
import { UEFA_IMG_BASE_URL } from 'utils/imgUefaBaseUrl';

export const LineUpsField = ({ match, previousMatch, isDesktop, fieldSize, competition }: LineUpsFieldData) => {
  const { t } = useTranslation();

  const {
    data: lineUps = {} as LineUp,
    isLoading: lineUpsLoading,
    isError: lineUpsError,
  } = useGetLineUpQuery(match.id);
  const {
    data: mainEvents = [] as Array<MatchEvents>,
    isLoading: mainEventsLoading,
    isError: mainEventsError,
  } = useGetMatchEventsQuery({ matchId: match.id, filter: 'MAIN', offset: 0, limit: 100 });

  if (lineUps?.lineupStatus === 'NOT_AVAILABLE') return <div>{t('official_line_ups_not_yet_available')}</div>;

  const matchCompetition = match && match.competition.code === 'UCL' ? 'UCL' : 'default';
  const fields = {
    UCL: `${UEFA_IMG_BASE_URL}/imgml/presskits/lineups/UCL2.min.jpg`,
    default: `${UEFA_IMG_BASE_URL}/imgml/presskits/lineups/defaultField.jpg`,
  };

  if (lineUpsLoading || lineUpsError || mainEventsLoading || mainEventsError) {
    return <div />;
  }

  if (Object.keys(lineUps).length === 0) {
    return <div />;
  }

  const { homeTeam, awayTeam } = lineUps;

  const coachBackground = match.competition.code === 'UCL' ? '#0A0A61' : '#5A9A61';

  const matchInfoBackground = match.competition.code === 'UCL' ? '#03004B' : '#497c4e';

  const coachBackgroundPrint =
    match.competition.code === 'UCL' ? 'pr-coach-ucl-background' : 'pr-coach-default-background';

  const matchInfoBackgroundPrint =
    match.competition.code === 'UCL' ? 'pr-match-info-ucl-background' : 'pr-match-info-default-background';

  const fieldWidth = isDesktop ? fieldSize.desktop : fieldSize.mobile;

  return (
    <div className="pk-m--0 pk-p--0">
      <div className={matchInfoBackgroundPrint} style={{ width: `${fieldWidth}%`, background: matchInfoBackground }}>
        {previousMatch ? <ShowMatchDetails match={match} /> : null}
        <div style={{ background: coachBackground }} className={coachBackgroundPrint}>
          <div>
            <DisplayTeamInfo team={homeTeam} isHomeTeam competition={competition} />
          </div>
          <div className="pk-position--relative">
            <img src={fields[matchCompetition]} alt="field" style={{ width: '100%' }} />
            <div>
              {homeTeam.field.map((player, i) => (
                <LineupPlayer
                  key={player?.player?.id ?? `home_${i}`}
                  player={player}
                  isHome
                  color={homeTeam.shirtColor}
                  competition={competition}
                  isDesktop={isDesktop}
                  fieldSize={fieldSize}
                  mainEvents={mainEvents}
                />
              ))}
              {awayTeam.field.map((player, i) => (
                <LineupPlayer
                  key={player?.player?.id ?? `away_${i}`}
                  player={player}
                  isHome={false}
                  color={awayTeam.shirtColor}
                  competition={competition}
                  isDesktop={isDesktop}
                  fieldSize={fieldSize}
                  mainEvents={mainEvents}
                />
              ))}
            </div>
          </div>
          <div>
            <DisplayTeamInfo team={awayTeam} isHomeTeam={false} competition={competition} />
          </div>
        </div>
      </div>
    </div>
  );
};
